import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Drawer,
  IconButton,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';
import { useImmigrationWorkflowDefinition } from './useImmigrationWorkflowDefinition';

const DEFAULT_STEP_LABELS = {
  new1_request_i9: '1. Request I-9 from candidate',
  new2_collect_i9: '2. Collect completed I-9',
  new3_validate_i9: '3. Validate I-9 internally',
  new4_everify_pending: '4. Submit in E-Verify (government site)',
  new5_everify_result: '5. Store E-Verify result',
  new_complete: 'Complete',
};

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return `${name} — ${c.email || ''}`;
}

function candName(row) {
  const c = row.candidate;
  if (!c) return '—';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
}

export default function EverifyNewList() {
  const { data: everifyDef } = useImmigrationWorkflowDefinition('everify_new');
  const STEP_LABELS = { ...DEFAULT_STEP_LABELS, ...(everifyDef?.stepLabels || {}) };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [openAdd, setOpenAdd] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [hireDateNew, setHireDateNew] = useState('');
  const [tentativeStart, setTentativeStart] = useState('');
  const [notesNew, setNotesNew] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [tentativeStartDate, setTentativeStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [wfBusy, setWfBusy] = useState(false);
  const [parseHint, setParseHint] = useState(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/immigration/everify/new-cases', { params: { page, limit: 25 } });
      setRows(res.data?.data || []);
      const p = res.data?.pagination;
      if (p) setPagination({ page: p.page, totalPages: p.totalPages, total: p.total });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load cases.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const loadCandidates = useCallback(async () => {
    setContactLoading(true);
    try {
      const res = await api.get('/contacts', {
        params: { contact_type: 'candidate', status: 'active', limit: 500, page: 1 },
      });
      setContactOptions(res.data?.data || []);
    } catch {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const openAddDialog = () => {
    setFormError('');
    setSelectedCandidate(null);
    setHireDateNew('');
    setTentativeStart('');
    setNotesNew('');
    setOpenAdd(true);
    loadCandidates();
  };

  const createCase = async () => {
    setFormError('');
    if (!selectedCandidate) {
      setFormError('Select a candidate.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/immigration/everify/new-cases', {
        candidate_contact_id: selectedCandidate.id,
        hire_date: hireDateNew || undefined,
        tentative_start_date: tentativeStart || undefined,
        notes: notesNew.trim() || undefined,
      });
      setOpenAdd(false);
      loadCases();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to create case.');
    } finally {
      setSaving(false);
    }
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    setDetailError('');
    setParseHint(null);
    try {
      const res = await api.get(`/immigration/everify/new-cases/${id}`);
      const d = res.data?.data;
      setDetail(d);
      setHireDate(d?.hire_date || '');
      setTentativeStartDate(d?.tentative_start_date || '');
      setNotes(d?.notes || '');
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Failed to load case.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDrawer = (id) => {
    setActiveId(id);
    setDrawerOpen(true);
    loadDetail(id);
  };

  const saveNotes = async () => {
    if (!activeId) return;
    setDetailError('');
    try {
      await api.patch(`/immigration/everify/new-cases/${activeId}`, {
        hire_date: hireDate || null,
        tentative_start_date: tentativeStartDate || null,
        notes: notes.trim() || null,
      });
      await loadDetail(activeId);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Save failed.');
    }
  };

  const wfAction = async (action) => {
    if (!activeId) return;
    setDetailError('');
    setWfBusy(true);
    try {
      await api.patch(`/immigration/everify/new-cases/${activeId}/workflow`, { action });
      await loadDetail(activeId);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Workflow update failed.');
    } finally {
      setWfBusy(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!activeId || !file) return;
    setDetailError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      const res = await api.post(`/immigration/everify/new-cases/${activeId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = res.data?.data;
      const hints = payload?.parse_hints;
      if (hints?.hire_date) setParseHint(hints);
      else setParseHint(null);
      const d = payload?.case || payload;
      setDetail(d);
      if (d) {
        setHireDate(d?.hire_date || '');
        setTentativeStartDate(d?.tentative_start_date || '');
        setNotes(d?.notes || '');
      }
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Upload failed.');
    }
  };

  const downloadDoc = async (documentId, fileName) => {
    if (!activeId || !documentId) return;
    try {
      const res = await api.get(
        `/immigration/everify/new-cases/${activeId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDetailError('Download failed.');
    }
  };

  const applyHireHint = () => {
    if (parseHint?.hire_date) setHireDate(parseHint.hire_date);
  };

  const step = detail?.workflow_step;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        E-Verify — New hire
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Guided checklist from requesting Form I-9 through validation, manual submission in the official E-Verify
        website, and storing the result PDF. The candidate does not log into this ERP: send them the official I-9
        (paper or your HRIS onboarding link), then upload the completed I-9 here when you receive it (email, secure
        file transfer, or onboarding portal).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
              New case
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Candidate</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell align="right">Open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No cases yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{candName(row)}</TableCell>
                        <TableCell>{row.candidate?.email || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={STEP_LABELS[row.workflow_step] || row.workflow_step} color={row.workflow_step === 'new_complete' ? 'success' : 'primary'} variant={row.workflow_step === 'new_complete' ? 'filled' : 'outlined'} />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => openDrawer(row.id)}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button size="small" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={openAdd} onClose={() => !saving && setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New E-Verify new-hire case</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contactOptions}
              loading={contactLoading}
              getOptionLabel={(o) => contactLabel(o)}
              value={selectedCandidate}
              onChange={(_, v) => setSelectedCandidate(v)}
              renderInput={(params) => (
                <TextField {...params} label="Candidate" size="small" helperText="Candidate contacts only." />
              )}
            />
            <TextField label="Hire date (if known)" type="date" size="small" InputLabelProps={{ shrink: true }} value={hireDateNew} onChange={(e) => setHireDateNew(e.target.value)} fullWidth />
            <TextField label="Tentative start date" type="date" size="small" InputLabelProps={{ shrink: true }} value={tentativeStart} onChange={(e) => setTentativeStart(e.target.value)} fullWidth />
            <TextField label="Notes" size="small" multiline minRows={2} value={notesNew} onChange={(e) => setNotesNew(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createCase} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">E-Verify new hire</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflow: 'auto' }}>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {!detailLoading && detail && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {candName({ candidate: detail.candidate })}
              </Typography>
              <Typography variant="body2">{detail.candidate?.email}</Typography>

              <Chip label={STEP_LABELS[step] || step} color={step === 'new_complete' ? 'success' : 'primary'} />

              <Alert severity="info" variant="outlined">
                Steps 4–5 assume your team uses the U.S. government E-Verify site; this app only records status and
                files, and does not connect to E-Verify.
              </Alert>

              <TextField label="Hire date" type="date" size="small" InputLabelProps={{ shrink: true }} value={hireDate} onChange={(e) => setHireDate(e.target.value)} fullWidth disabled={step === 'new_complete'} />
              <TextField label="Tentative start date" type="date" size="small" InputLabelProps={{ shrink: true }} value={tentativeStartDate} onChange={(e) => setTentativeStartDate(e.target.value)} fullWidth disabled={step === 'new_complete'} />
              <TextField label="Notes" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} disabled={step === 'new_complete'} />
              {step !== 'new_complete' && (
                <Button variant="outlined" onClick={saveNotes}>
                  Save dates & notes
                </Button>
              )}

              <Divider />

              {step === 'new1_request_i9' && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Send the employee section of Form I-9 (and your state instructions if any) to the candidate using
                    your standard process. When the request is sent, continue to the next step.
                  </Typography>
                  <Button variant="contained" disabled={wfBusy} onClick={() => wfAction('advance_after_request')}>
                    I-9 request sent to candidate
                  </Button>
                </Stack>
              )}

              {step === 'new2_collect_i9' && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    When the candidate returns a signed I-9 (scan or photo), upload it here. Then move to validation.
                  </Typography>
                  {parseHint?.hire_date && (
                    <Alert severity="info" action={<Button onClick={applyHireHint}>Apply hire date</Button>}>
                      Suggested hire date from PDF: {parseHint.hire_date}
                    </Alert>
                  )}
                  <Button variant="outlined" component="label">
                    Upload / replace I-9
                    <input type="file" hidden accept=".pdf,image/*" onChange={(e) => uploadFile(e.target.files?.[0], 'i9')} />
                  </Button>
                  {detail.i9Document && (
                    <Button startIcon={<DownloadIcon />} size="small" onClick={() => downloadDoc(detail.i9Document.id, detail.i9Document.file_name)}>
                      {detail.i9Document.file_name}
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Requested: {detail.i9_requested_at ? new Date(detail.i9_requested_at).toLocaleString() : '—'}
                  </Typography>
                  <Button variant="contained" disabled={wfBusy || !detail.i9_document_id} onClick={() => wfAction('advance_to_validation')}>
                    I-9 received — proceed to HR validation
                  </Button>
                </Stack>
              )}

              {step === 'new3_validate_i9' && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Confirm lists A / B / C documents and Section 2 per your policy. When satisfied, advance to E-Verify
                    submission.
                  </Typography>
                  {detail.i9Document && (
                    <Button startIcon={<DownloadIcon />} size="small" onClick={() => downloadDoc(detail.i9Document.id, detail.i9Document.file_name)}>
                      {detail.i9Document.file_name}
                    </Button>
                  )}
                  <Button variant="contained" disabled={wfBusy} onClick={() => wfAction('advance_after_validation')}>
                    I-9 validated — ready for E-Verify
                  </Button>
                </Stack>
              )}

              {step === 'new4_everify_pending' && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Create the case in the E-Verify website with the same hire data. When the case is submitted there,
                    mark it here.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Validated: {detail.i9_validated_at ? new Date(detail.i9_validated_at).toLocaleString() : '—'}
                  </Typography>
                  <Button variant="contained" disabled={wfBusy} onClick={() => wfAction('advance_after_everify_submit')}>
                    Submitted in E-Verify portal
                  </Button>
                </Stack>
              )}

              {step === 'new5_everify_result' && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Download the case result from E-Verify (PDF) and attach it here. Then close the case.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submitted: {detail.everify_submitted_at ? new Date(detail.everify_submitted_at).toLocaleString() : '—'}
                  </Typography>
                  <Button variant="outlined" component="label">
                    Upload / replace result PDF
                    <input type="file" hidden accept="application/pdf,.pdf" onChange={(e) => uploadFile(e.target.files?.[0], 'everify_result')} />
                  </Button>
                  {detail.everifyResultDocument && (
                    <Button startIcon={<DownloadIcon />} size="small" onClick={() => downloadDoc(detail.everifyResultDocument.id, detail.everifyResultDocument.file_name)}>
                      {detail.everifyResultDocument.file_name}
                    </Button>
                  )}
                  <Button variant="contained" disabled={wfBusy || !detail.everify_result_document_id} onClick={() => wfAction('advance_to_complete')}>
                    Close case — verification on file
                  </Button>
                </Stack>
              )}

              {step === 'new_complete' && (
                <Alert severity="success">This workflow is complete.</Alert>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
